import React, { type ReactNode, useState } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	fireUIAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics-component';
import type { addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_devOpsFeatureFlagProviders$key } from '@atlassian/jira-relay/src/__generated__/addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_devOpsFeatureFlagProviders.graphql';
import type { addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssue.graphql';
import type { addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssueSummary$key } from '@atlassian/jira-relay/src/__generated__/addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssueSummary.graphql';
import { lazy } from '@atlassian/react-loosely-lazy';
import { VIEWPORT_WIDTH_MEDIUM } from '../../../../../common/constants.tsx';
import { ActionButtonNoProviderAsync } from './action-button-no-provider/async.tsx';
import { AddFeatureFlagIcon } from './icon/AddFeatureFlagIcon.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ActionMenu = lazy(() =>
	import(
		/* webpackChunkName: "async-jira-software-releases-add-feature-flag-action-menu" */ './action-menu'
	).then((module) => module.ActionMenu),
);

// Extract just the content excluding button so we don't need to worry about passing props and
// forwardRef
const TriggerContent = ({ children }: { children: ReactNode }) => (
	<ButtonContainer>
		<AddFeatureFlagIcon />
		<ButtonText>{children}</ButtonText>
	</ButtonContainer>
);

/**
 * Component to be rendered when a user has at least one feature flag provider installed
 * Allows the user to perform feature flag provider specific actions and open the installation modal
 */
export const AddFeatureFlagDropdown = ({
	jiraIssue,
	jiraIssueSummary,
	devOpsFeatureFlagProviders,
	onOpenFeatureFlagEmptyStateModal,
}: {
	jiraIssue: addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssue$key;
	jiraIssueSummary: addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssueSummary$key;
	devOpsFeatureFlagProviders: addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_devOpsFeatureFlagProviders$key;
	onOpenFeatureFlagEmptyStateModal: () => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssue on JiraIssue {
				...actionMenu_softwareReleasesVersionDetailIssueList_jiraIssue
			}
		`,
		jiraIssue,
	);

	const issueSummary = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssueSummary on JiraSingleLineTextField {
				...actionMenu_softwareReleasesVersionDetailIssueList_jiraIssueSummary
			}
		`,
		jiraIssueSummary,
	);

	const featureFlagProviders = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_devOpsFeatureFlagProviders on DevOpsFeatureFlagProvider
			@relay(plural: true) {
				...actionMenu_softwareReleasesVersionDetailIssueList_devOpsFeatureFlagProviders
			}
		`,
		devOpsFeatureFlagProviders,
	);

	return (
		<OnHoverContainer isVisible={isDropdownOpen}>
			<Placeholder
				name="action-menu"
				fallback={
					<Button appearance="subtle-link" spacing="none" isDisabled>
						<TriggerContent>{formatMessage(messages.addFeatureFlag)}</TriggerContent>
					</Button>
				}
			>
				<ActionMenu
					jiraIssue={issue}
					jiraIssueSummary={issueSummary}
					onOpenChange={({ isOpen }) => setIsDropdownOpen(isOpen)}
					devOpsFeatureFlagProviders={featureFlagProviders}
					onOpenFeatureFlagEmptyStateExperience={onOpenFeatureFlagEmptyStateModal}
					trigger={({ triggerRef, ...props }) => (
						<Button appearance="subtle-link" spacing="none" ref={triggerRef} {...props}>
							<TriggerContent>{formatMessage(messages.addFeatureFlag)}</TriggerContent>
						</Button>
					)}
				/>
			</Placeholder>
		</OnHoverContainer>
	);
};

/**
 * Component to be rendered when a user does not have any feature flag providers installed
 * Allows the user to open the installation modal
 */
export const AddFeatureFlagButton = ({
	onOpenFeatureFlagEmptyStateModal,
	rowNum,
	featureFlagButtonRef,
}: {
	onOpenFeatureFlagEmptyStateModal: () => void;
	rowNum: number;
	featureFlagButtonRef: React.RefObject<HTMLButtonElement>;
}): React.JSX.Element => {
	const spotlightButton = (
		<Placeholder
			name="action-button-no-provider"
			fallback={
				<Button appearance="subtle-link" spacing="none" isDisabled>
					<AddFeatureFlagIcon />
				</Button>
			}
		>
			<ActionButtonNoProviderAsync
				emptyStateButtonRef={featureFlagButtonRef}
				onClick={(_, analyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'versionDetailAddFeatureFlagButton');
					onOpenFeatureFlagEmptyStateModal();
				}}
			>
				<AddFeatureFlagIcon />
			</ActionButtonNoProviderAsync>
		</Placeholder>
	);

	const regularButton = (
		<Button
			ref={featureFlagButtonRef}
			appearance="subtle-link"
			spacing="none"
			onClick={(_, analyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'versionDetailAddFeatureFlagButton');
				onOpenFeatureFlagEmptyStateModal();
			}}
		>
			<AddFeatureFlagIcon />
		</Button>
	);

	return (
		<AddFeatureFlagButtonWrapper>
			<ContextualAnalyticsData
				sourceName="versionDetailsTableAddFeatureFlagActionButton"
				sourceType={SCREEN}
			>
				{rowNum === 1 ? spotlightButton : regularButton}
				{/* We don't want to fire screen event too many times */}
				{rowNum === 1 ? <FireScreenAnalytics /> : null}
			</ContextualAnalyticsData>
		</AddFeatureFlagButtonWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonText = styled.div({
	paddingRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (max-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (min-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		display: 'inline',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddFeatureFlagButtonWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

type OnHoverContainerProps = {
	isVisible: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OnHoverContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: (props: OnHoverContainerProps) => (props.isVisible ? 'visible !important' : 'hidden'),
});
